import { useEffect, useMemo, useState } from 'react';
import { Alert, Container, Link } from '@backstage/ui';
import { useApi, useRouteRef } from '@backstage/frontend-plugin-api';
import type { Entity } from '@backstage/catalog-model';
import {
  catalogApiRef,
  EntityDisplayName,
  entityRouteParams,
  entityRouteRef,
} from '@backstage/plugin-catalog-react';
import {
  scaffolderApiRef,
  type ScaffolderTask,
} from '@backstage/plugin-scaffolder-react';
import type { AppTypeDetail } from '../shared/appTypes';
import { roleOfTemplate, useAppTypeDescriptors } from '../shared/templateRoles';
import {
  loadTask,
  taskProject,
  type TaskProject,
  type TaskProjectAction,
} from './taskProject';

const ACTION_LABELS: Record<TaskProjectAction, string> = {
  deletion: 'suppression réelle',
  simulation: 'simulation',
  creation: 'création',
  wake: 'réveil',
  sleep: 'mise en sommeil',
  promote: 'promotion',
  configure: 'configuration',
  restore: 'restauration à un instant',
};

const NO_APP_TYPES: readonly AppTypeDetail[] = [];

interface ResolvedTaskProject extends TaskProject {
  /** The project's catalog entity, when the catalog still has it. */
  entity?: Entity;
}

/**
 * « Projet : odoodemo3 — suppression réelle » (« — réveil de preprod » for
 * the wake of an environment, « — promotion dev → preprod » for a promotion)
 * above a scaffolder task page:
 * the template's title (« Supprimer un projet Odoo ») does not say which
 * project. Only for the Odoo project templates (`taskProject.ts`); nothing for
 * any other template, nor while the task loads or when it cannot be read.
 *
 * The project name links to `component:default/<projet>` when the catalog
 * has it and its entity page is mounted (same approach as
 * `ProjectCreationLine.tsx`); plain text otherwise — a deleted project has no
 * entity left. The banner appears once the catalog has answered, so the name
 * does not switch from text to link under the reader's eyes.
 *
 * A real deletion is in the danger tone; a simulation and a creation in the
 * neutral one.
 */
export function TaskProjectBanner(props: { taskId: string }) {
  const { taskId } = props;
  const scaffolderApi = useApi(scaffolderApiRef);
  const catalogApi = useApi(catalogApiRef);
  const entityRoute = useRouteRef(entityRouteRef);
  const linkable = entityRoute !== undefined;
  const [task, setTask] = useState<ScaffolderTask>();
  const [resolved, setResolved] = useState<ResolvedTaskProject>();

  useEffect(() => {
    let active = true;
    setTask(undefined);
    loadTask(scaffolderApi, taskId).then(
      found => {
        if (active) setTask(found);
      },
      () => {
        // The task page reports its own failure to read the task.
      },
    );
    return () => {
      active = false;
    };
  }, [scaffolderApi, taskId]);

  // A template whose role its name does not say: the descriptors may
  // designate it (`taskProject`).
  const descriptors = useAppTypeDescriptors({
    enabled:
      task !== undefined &&
      roleOfTemplate(task.spec.templateInfo?.entityRef) === undefined,
  });
  const appTypes =
    descriptors.status === 'ready' ? descriptors.items : NO_APP_TYPES;
  const about = useMemo(
    () => (task ? taskProject(task, appTypes) : undefined),
    [task, appTypes],
  );

  useEffect(() => {
    let active = true;
    setResolved(undefined);
    if (!about) return undefined;
    const load = async () => {
      let entity: Entity | undefined;
      if (linkable) {
        try {
          entity = await catalogApi.getEntityByRef({
            kind: 'component',
            namespace: 'default',
            name: about.project,
          });
        } catch {
          // Unreadable catalog: the name stays, as plain text.
        }
      }
      if (active) setResolved({ ...about, entity });
    };
    // `load` turns its own failures into « nothing to show », so nothing can
    // reject.
    void load();
    return () => {
      active = false;
    };
  }, [catalogApi, about, linkable]);

  if (!resolved) return null;

  const { project, action, entity, environment, source } = resolved;
  let subject = '';
  if (source && environment) {
    subject = ` ${source} → ${environment}`;
  } else if (environment) {
    subject = ` de ${environment}`;
  }
  const projectName =
    entity && entityRoute ? (
      <Link href={entityRoute(entityRouteParams(entity))}>
        <strong>
          <EntityDisplayName entityRef={entity} hideIcon />
        </strong>
      </Link>
    ) : (
      <strong>{project}</strong>
    );

  return (
    <Container>
      <Alert
        mt="4"
        status={action === 'deletion' ? 'danger' : 'info'}
        icon
        data-action={action}
        title={
          <>
            Projet : {projectName} — {ACTION_LABELS[action]}
            {subject}
          </>
        }
      />
    </Container>
  );
}
